import type { CollisionDetection } from '@dnd-kit/core';
import { closestCenter, pointerWithin, rectIntersection } from '@dnd-kit/core';

export const getCollapsedNamesAfterFirst = (sections: Array<{ name?: string | null }> | undefined, keepExpandedName?: string): string[] =>
  (sections ?? [])
    .slice(1)
    .map((section) => section.name)
    .filter((name): name is string => !!name)
    .filter((name) => name !== keepExpandedName);

type NamedTabSection = { name?: string | null; tabId?: string | null };

const matchesSection = (section: NamedTabSection, id: string, tabId?: string | null) =>
  section.name === id && (section.tabId ?? null) === (tabId ?? null);

export const getSectionReorderIndexes = (
  sections: NamedTabSection[],
  draggedId: string,
  overId: string,
  draggedTabId?: string | null,
  overTabId?: string | null
): { oldIndex: number; newIndex: number } | null => {
  if ((draggedTabId ?? null) !== (overTabId ?? null)) return null;

  const oldIndex = sections.findIndex((section) => matchesSection(section, draggedId, draggedTabId));
  const newIndex = sections.findIndex((section) => matchesSection(section, overId, overTabId));

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return null;

  return { oldIndex, newIndex };
};

export const createFormSectionCollisionDetection = (sortableTypes: readonly string[]): CollisionDetection => {
  const sortableTypeSet = new Set(sortableTypes);

  return (args) => {
    const activeData = args.active.data.current;
    const activeType = activeData?.type;

    if (typeof activeType === 'string' && sortableTypeSet.has(activeType)) {
      const restrictToTab = activeData != null && 'tabId' in activeData;
      const activeTabId = activeData?.tabId ?? null;

      const droppableContainers = args.droppableContainers.filter((container) => {
        if (container.id === args.active.id || container.data.current?.type !== activeType) return false;
        if (!restrictToTab) return true;

        return (container.data.current?.tabId ?? null) === activeTabId;
      });

      return closestCenter({ ...args, droppableContainers });
    }

    const pointerCollisions = pointerWithin(args);

    return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
  };
};
